import { motion } from "framer-motion";
import { Users, CheckCircle2, AlertTriangle, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  PieChart as RechartPie, Pie, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { Button } from "../ui/button";
import { downloadVoterTracking } from "../../lib/api";
import { translateSegment } from "./dataUtils";
import { toast } from "sonner";

const AGE_SEGMENTS = [
  'Menors de 20 anys', 'de 20 a 29 anys', 'de 30 a 39 anys',
  'de 40 a 49 anys', 'de 50 a 59 anys', 'de 60 a 69 anys',
  'de 70 a 79 anys', 'Majors de 79 anys'
];

export function ParticipationTab({ results, consulta, id }) {
  const { t } = useTranslation();

  const bySegment = results?.participation?.by_segment || [];
  const segmentData = {};
  bySegment.forEach(item => {
    const age = item.age_segment;
    if (!segmentData[age]) segmentData[age] = { dona: { voted: 0, census: 0, rate: 0 }, home: { voted: 0, census: 0, rate: 0 } };
    if (item.sex === 'Dona') segmentData[age].dona = { voted: item.voted, census: item.census, rate: item.participation_rate };
    else if (item.sex === 'Home') segmentData[age].home = { voted: item.voted, census: item.census, rate: item.participation_rate };
  });

  let totalDonaCensus = 0, totalDonaVoted = 0, totalHomeCensus = 0, totalHomeVoted = 0;
  Object.values(segmentData).forEach(data => {
    totalDonaCensus += data.dona.census || 0;
    totalDonaVoted += data.dona.voted || 0;
    totalHomeCensus += data.home.census || 0;
    totalHomeVoted += data.home.voted || 0;
  });

  const handleDownloadTracking = async () => {
    try {
      const res = await downloadVoterTracking(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      link.setAttribute('download', filenameMatch ? filenameMatch[1] : `participacio_${consulta?.title?.replace(/ /g, '_') || 'consulta'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('results.fileDownloaded'));
    } catch {
      toast.error(t('results.fileDownloadError'));
    }
  };

  return (
    <div className="space-y-6" data-testid="participation-tab">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg"><Users className="h-5 w-5 text-blue-600" /></div>
            <span className="text-sm text-slate-500">{t('results.totalCensus')}</span>
          </div>
          <p className="text-3xl font-bold text-slate-900">{results?.participation?.total_census || 0}</p>
          <p className="text-xs text-slate-400 mt-1">{t('results.personsWithVotingRight')}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-100 rounded-lg"><CheckCircle2 className="h-5 w-5 text-green-600" /></div>
            <span className="text-sm text-slate-500">{t('results.haveVoted')}</span>
          </div>
          <p className="text-3xl font-bold text-green-600">{results?.participation?.voted || 0}</p>
          <p className="text-xs text-slate-400 mt-1">{results?.participation?.participation_rate || 0}% {t('results.ofParticipation')}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-orange-100 rounded-lg"><AlertTriangle className="h-5 w-5 text-orange-600" /></div>
            <span className="text-sm text-slate-500">{t('results.abstention')}</span>
          </div>
          <p className="text-3xl font-bold text-orange-600">{results?.participation?.abstention || 0}</p>
          <p className="text-xs text-slate-400 mt-1">{results?.participation?.abstention_rate || 0}% {t('results.ofAbstention')}</p>
        </motion.div>
      </div>

      {/* Pie Chart */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
        className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">{t('results.participationDistribution')}</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <RechartPie>
              <Pie
                data={[
                  { name: t('results.haveVoted'), value: results?.participation?.voted || 0, fill: '#22c55e' },
                  { name: t('results.abstention'), value: results?.participation?.abstention || 0, fill: '#f97316' }
                ]}
                cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
              />
              <Tooltip formatter={(value, name) => [value + ' ' + t('results.personsLabel'), name]}
                contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }} />
              <Legend />
            </RechartPie>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Stats Summary Table */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
        className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">{t('results.statisticSummary')}</h3>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>{t('results.concept')}</th><th className="text-right">{t('results.quantity')}</th><th className="text-right">{t('results.percentageColumn')}</th></tr></thead>
            <tbody>
              <tr>
                <td className="font-medium"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-blue-500" />{t('results.totalElectoralCensus')}</div></td>
                <td className="text-right font-mono">{results?.participation?.total_census || 0}</td>
                <td className="text-right font-mono">100%</td>
              </tr>
              <tr className="bg-green-50">
                <td className="font-medium"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-green-500" />{t('results.participationVoted')}</div></td>
                <td className="text-right font-mono font-semibold text-green-600">{results?.participation?.voted || 0}</td>
                <td className="text-right font-mono font-semibold text-green-600">{results?.participation?.participation_rate || 0}%</td>
              </tr>
              <tr className="bg-orange-50">
                <td className="font-medium"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-orange-500" />{t('results.abstentionNotVoted')}</div></td>
                <td className="text-right font-mono font-semibold text-orange-600">{results?.participation?.abstention || 0}</td>
                <td className="text-right font-mono font-semibold text-orange-600">{results?.participation?.abstention_rate || 0}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Detail by Age and Sex */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
        className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">{t('results.participationDetailTitle')}</h3>
        <p className="text-sm text-slate-500 mb-4">{t('results.participationOnCensus')}</p>
        <div className="overflow-x-auto">
          <table className="data-table text-sm">
            <thead>
              <tr>
                <th></th>
                <th className="text-center"><div className="flex items-center justify-center gap-1"><div className="w-2 h-2 rounded-full bg-pink-500" />{t('results.woman')}</div></th>
                <th className="text-center"><div className="flex items-center justify-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500" />{t('results.man')}</div></th>
                <th className="text-center bg-slate-100">{t('results.total')}</th>
              </tr>
            </thead>
            <tbody>
              {AGE_SEGMENTS.map(segment => {
                const data = segmentData[segment] || { dona: { voted: 0, census: 0, rate: 0 }, home: { voted: 0, census: 0, rate: 0 } };
                const tc = (data.dona.census || 0) + (data.home.census || 0);
                const tv = (data.dona.voted || 0) + (data.home.voted || 0);
                const tr = tc > 0 ? ((tv / tc) * 100).toFixed(1) : '0.0';
                return (
                  <tr key={segment}>
                    <td className="font-medium">{translateSegment(t, segment.replace(/ anys$/i, ''))}</td>
                    <td className="text-center">
                      <span className="text-pink-600 font-semibold">{data.dona.rate || '0.0'}%</span>
                      <span className="text-xs text-slate-400 block">({data.dona.voted || 0}/{data.dona.census || 0})</span>
                    </td>
                    <td className="text-center">
                      <span className="text-blue-600 font-semibold">{data.home.rate || '0.0'}%</span>
                      <span className="text-xs text-slate-400 block">({data.home.voted || 0}/{data.home.census || 0})</span>
                    </td>
                    <td className="text-center bg-slate-50 font-bold">
                      {tr}%<span className="text-xs text-slate-400 block font-normal">({tv}/{tc})</span>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-slate-100 font-bold border-t-2 border-slate-300">
                <td>{t('results.total')}</td>
                <td className="text-center text-pink-600">
                  {totalDonaCensus > 0 ? ((totalDonaVoted / totalDonaCensus) * 100).toFixed(1) : '0.0'}%
                  <span className="text-xs text-slate-500 block font-normal">({totalDonaVoted}/{totalDonaCensus})</span>
                </td>
                <td className="text-center text-blue-600">
                  {totalHomeCensus > 0 ? ((totalHomeVoted / totalHomeCensus) * 100).toFixed(1) : '0.0'}%
                  <span className="text-xs text-slate-500 block font-normal">({totalHomeVoted}/{totalHomeCensus})</span>
                </td>
                <td className="text-center bg-slate-200">
                  {(totalDonaCensus + totalHomeCensus) > 0 ? (((totalDonaVoted + totalHomeVoted) / (totalDonaCensus + totalHomeCensus)) * 100).toFixed(1) : '0.0'}%
                  <span className="text-xs text-slate-500 block font-normal">({totalDonaVoted + totalHomeVoted}/{totalDonaCensus + totalHomeCensus})</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-xs text-slate-600">
            <span className="font-semibold">{t('results.legend')}</span> {t('results.legendFormat')}
          </p>
        </div>
      </motion.div>

      {/* Voter Tracking Download */}
      {consulta?.enable_voter_tracking && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
          className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="voter-tracking-download-section">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-heading font-semibold text-slate-900">{t('results.participationControl')}</h3>
              <p className="text-sm text-slate-500 mt-1">{t('results.downloadDniList')}</p>
            </div>
            <Button onClick={handleDownloadTracking} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-tracking-btn">
              <Download className="h-4 w-4 mr-2" />{t('results.downloadXlsx')}
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
